import { getAuthToken } from '@/lib/api/apiClient'
import { getBrowserApiBaseUrl } from '@/config/env'
import {
  openServeFileInNewTabFromGesture as openServeFileInNewTab,
  triggerServeFileDownload,
} from '@/lib/utils/serveFileHelpers'
import {
  applyFreshAccessTokenToUrl,
  generateClaimsFilePreviewUrl,
  getFileExtension,
  isImageFile,
} from '@/lib/utils/filePreview'
import { fileTokenService } from '@/lib/services/fileTokenService'
import { buildResidentClaimsDownloadPath, encodeClaimsFilePath } from './residentDocuments'

/** Angular `downloadNoteFile` opens these extensions directly; other types go through the viewer. */
const NON_IMAGE_PREVIEW_EXTENSIONS = ['pdf', 'xlsx', 'csv', 'doc', 'docx', 'txt']

export function buildServeFileUrl(module: string, refId: string | number, fileType: string, fileId: string | number): string {
  return `${getBrowserApiBaseUrl()}/serve-file/${module}/${refId}/${fileType}/${fileId}`
}

export async function downloadBlobWithToken(url: string): Promise<Blob> {
  const response = await fetch(url, {
    headers: getAuthToken() ? { Token: String(getAuthToken()) } : undefined,
  })
  if (!response.ok) throw new Error(`Download failed with status ${response.status}`)
  return response.blob()
}

export function openBlob(blob: Blob): void {
  const objectUrl = URL.createObjectURL(blob)
  window.open(objectUrl, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000)
}

export function openApiPath(path: string): void {
  const base = getBrowserApiBaseUrl()
  window.open(`${base}${path}`, '_blank', 'noopener,noreferrer')
}

type LibertyDocPreviewSource = {
  id?: string | number
  attachment_id?: string | number
  attachment_path?: string
  attachment_name?: string
}

/**
 * Build `/file-handlers` filesList for liberty-document image swipe (applicant-style).
 * Only returns a list when there are 2+ images; PDFs/docs are excluded.
 * Call after `ensureFileToken()` so URLs include a valid access_token.
 */
export function buildLibertyDocumentImagePreviewList(
  docs: LibertyDocPreviewSource[],
  fileType = 'LIBERTY_DOCUMENT',
): string[] | undefined {
  const urls: string[] = []
  for (const doc of docs) {
    const fileId = doc.id ?? doc.attachment_id
    const path = doc.attachment_path
    if (fileId == null || fileId === '' || !path) continue
    const name = String(doc.attachment_name || path || '')
    if (!isImageFile(name)) continue
    urls.push(generateClaimsFilePreviewUrl(fileId, fileType, encodeClaimsFilePath(String(path))))
  }
  return urls.length > 1 ? urls : undefined
}

/**
 * Liberty Document / note-file view (Angular `downloadNoteFile`): opens the token-secured
 * `/serve-file-claims-preview/{id}?fileType=...&url=...&access_token=...` endpoint. Non-image docs
 * open directly; images route through the file-handlers viewer.
 *
 * @param filesList - Optional sibling image preview URLs for left/right navigation (reimbursement/
 *   resident liberty docs). Do not use from applicant flows.
 */
export async function openResidentClaimsPreview(
  fileType: string,
  fileId: string | number,
  filepath: string,
  fileName = '',
  filesList?: string[],
): Promise<void> {
  if (!fileId || !filepath) return
  const token = await fileTokenService.ensureFileToken()
  const encodedPath = encodeClaimsFilePath(filepath)
  const secureUrl = generateClaimsFilePreviewUrl(fileId, fileType, encodedPath)
  const extension = getFileExtension(fileName || filepath).toLowerCase()
  if (NON_IMAGE_PREVIEW_EXTENSIONS.includes(extension)) {
    window.open(secureUrl, '_blank', 'noopener,noreferrer')
    return
  }

  // Same pattern as applicant `openFilePreview`: pass sibling image URLs for swipe prev/next.
  const listWithFreshToken =
    filesList && filesList.length > 1 && token
      ? filesList.map((url) => applyFreshAccessTokenToUrl(url, token))
      : filesList && filesList.length > 1
        ? filesList
        : undefined

  let viewerUrl =
    `/file-handlers?path=${encodeURIComponent(secureUrl)}` +
    (fileName ? `&name=${encodeURIComponent(fileName)}` : '')
  if (listWithFreshToken?.length) {
    viewerUrl += `&filesList=${encodeURIComponent(JSON.stringify(listWithFreshToken))}`
  }
  window.open(viewerUrl, '_blank', 'noopener,noreferrer')
}

/**
 * Force-download a Liberty / notes file via the claims-preview URL (blob + `download` attr).
 */
export async function downloadResidentClaimsPreview(
  fileType: string,
  fileId: string | number,
  filepath: string,
  fileName = '',
): Promise<void> {
  if (!fileId || !filepath) return
  await fileTokenService.ensureFileToken()
  const encodedPath = encodeClaimsFilePath(filepath)
  const secureUrl = generateClaimsFilePreviewUrl(fileId, fileType, encodedPath)
  const safeName =
    (fileName || filepath.split('/').pop() || 'document').trim() || 'document'
  await triggerServeFileDownload(secureUrl, safeName)
}

/** Open reimbursement default file (Angular `downloadClaimsFile`) in a new tab. */
export async function openResidentClaimsDocument(
  fileTypeKey: string,
  attachmentId: string | number,
  filepath: string,
): Promise<void> {
  const claimsPath = buildResidentClaimsDownloadPath(fileTypeKey, attachmentId, filepath)
  if (claimsPath) {
    await openServeFileInNewTab(claimsPath)
    return
  }
  if (filepath.startsWith('http://') || filepath.startsWith('https://')) {
    window.open(filepath, '_blank', 'noopener,noreferrer')
    return
  }
  openApiPath(filepath.startsWith('/') ? filepath : `/${filepath}`)
}

/** Force-download a claims file (blob + `download` attr). */
export async function downloadResidentClaimsDocument(
  fileTypeKey: string,
  attachmentId: string | number,
  filepath: string,
  fileName = '',
): Promise<void> {
  const claimsPath = buildResidentClaimsDownloadPath(fileTypeKey, attachmentId, filepath)
  if (!claimsPath) {
    throw new Error('Missing file information for download')
  }
  const safeName =
    (fileName || filepath.split('/').pop() || 'document').trim() || 'document'
  await triggerServeFileDownload(claimsPath, safeName)
}
